import * as React from 'react';

import {useNavigate} from 'react-router-dom';
import { useMessage } from '../context/MessageProvider';
import {useLoaderData} from 'react-router-dom';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import {useForm, FormProvider} from 'react-hook-form';
import Form from '../components/form/Form';
import FormButton from '../components/form/FormButton';
import FormSlider from '../components/form/FormSlider';
import FormRadioGroup from '../components/form/FormRadioGroup';
import FormTextField from '../components/form/FormTextField';
import FormConditionCheck from '../components/form/FormConditionCheck';

import Header from "../components/Header";
import Subtitle from "../components/Subtitle";

import { stereotypyFrequency, stereotypiesInterruptBehaviorFrequency, yesNoUnknown } from '../models/categories';

import CustomTooltip from '../components/tooltip/CustomTooltip';
import HelpIcon from '@mui/icons-material/Help';

import { getAssessment } from '../models/assessments';
import { getFinalEvaluation, newFinalEvaluation, saveFinalEvaluation } from '../models/finalevaluation';
import { listSectionBsByAssessment} from '../models/sectionb';

import { useTheme } from '@mui/material/styles';

import {today} from '../utils/datetime';

import { useAuth } from '../context/AuthProvider';
import { useConnectivity } from '../context/ConnectivityProvider';

/*
returns a boolean if the section b observations have any stereotyping behavior observations
*/
const hasStereotyping = (sectionBs) => {
    const result = sectionBs.reduce((acc,sectionB)=>{
        const found = sectionB.behaviors.filter((cur)=>{
            return cur.behavior === "stereotyping";
        });
        if (found.length > 0) acc = true;
        return acc;
    },false);
    return result;
}

const CustomSlider = ({name,label,leftLabel,rightLabel,tooltipKey,...props}) => {
    const theme = useTheme();

    const leftLabelComponent = (typeof leftLabel === "string") ?  <Typography variant="body2" sx={{color: theme.palette.text.secondary}}>{leftLabel}</Typography> : leftLabel;
    const rightLabelComponent = (typeof rightLabel === "string") ?  <Typography variant="body2" sx={{color: theme.palette.text.secondary}}>{rightLabel}</Typography> : rightLabel;

    return (
        <Grid container spacing={0} columnSpacing={3}>
            <Grid xs={12}>
                <FormSlider 
                    containerProps={{sx: {width: '100%'}}}
                    name={name} 
                    label={<Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
                            <Typography>{label}</Typography>
                            <CustomTooltip keyString={tooltipKey}>
                                <HelpIcon />
                            </CustomTooltip>
                        </Stack>}
                
                    min={0} 
                    max={10} 
                    step={0.1} 
                    marks={[]}
                {...props}
                />
            </Grid>
            <Grid xs={6}>
                {leftLabelComponent}
            </Grid>
            <Grid xs={6} sx={{textAlign: 'right'}}>
                {rightLabelComponent}
            </Grid>
        </Grid>
    );
}

export async function loader({params}) {
    return {
        stereotypyFrequency: await stereotypyFrequency(),
        stereotypiesInterruptBehaviorFrequency: await stereotypiesInterruptBehaviorFrequency(),
        yesNoUnknown: await yesNoUnknown(),
        assessment: await getAssessment(params.assessmentId),
        finalevaluation: await getFinalEvaluation(params.assessmentId) || newFinalEvaluation(),
        sectionBs:await listSectionBsByAssessment(params.assessmentId),
    };
}

export default function AssessmentFinalEvaluationFormPage({title = "EWP",...props}) {

    const {setSuccess, setError} = useMessage();
    const navigate = useNavigate();

    const {isOnline} = useConnectivity();
    const {user} = useAuth();

    const {stereotypyFrequency, stereotypiesInterruptBehaviorFrequency, yesNoUnknown, assessment, finalevaluation, sectionBs} = useLoaderData(); 
    console.log("stereotypeFrequency: ", stereotypyFrequency);
    console.log("stereotypiesInterruptBehaviorFrequency: ", stereotypiesInterruptBehaviorFrequency);
    console.log("yesNoUnknown: ", yesNoUnknown);
    console.log("finalevaluation",finalevaluation);
    console.log("sectionBs", sectionBs);

    const observedStereotyping = hasStereotyping(sectionBs);
    console.log("observedStereotyping", observedStereotyping);

    const save = async(inputData) => {
        const payload = {
            ...inputData,
            id: inputData.id || assessment.id,
        };
        console.log("final evaluation payload: ", payload);
        const saveResult = await saveFinalEvaluation(payload,user.id,isOnline);
        console.log("final evaluation save:", saveResult);
        return saveResult;
    }


    // if you're going to do a form submission, this is your starting point for the submit behavior
    const onSubmit = async (inputData) => {
        try {

            // make sure that the id set is the assessment ID, since there is a 1-1 relationship
            console.log("onSubmit write", inputData);
            const payload = {...inputData, completed_on: today()};
            await save(payload);

            setSuccess("Saved Final Evaluation");
            navigate("/assessments/" + assessment.id + "/submit");
        } catch (error) {
            console.error(error);
            setError(error);
        }
    }

    const saveDebounceRef = React.useRef(null);
    const onChange = (inputData) => {
        console.log("onChange write", inputData);
        if (saveDebounceRef.current) {
            clearTimeout(saveDebounceRef.current);
        }
        saveDebounceRef.current = setTimeout(() => {
            save(inputData);
        }, 400);
    }

    React.useEffect(() => {
        return () => {
            if (saveDebounceRef.current) {
                clearTimeout(saveDebounceRef.current);
            }
        };
    }, []);


    // handles the form steps in the UI
    const [page,setPage] = React.useState(1);
    const handleNext = () => {
        setPage(page + 1);
    }
    const handlePrev = () => {
        setPage(page - 1);
    }


    const defaultValues = {...newFinalEvaluation()};
    const memoizedValues = React.useMemo(() => ({...finalevaluation}), [finalevaluation]);
    const formProps = { defaultValues, values: memoizedValues };
	const methods = useForm(formProps);


    const overallPageReference = (observedStereotyping) ? 2 : 1;
    const maxPages = (observedStereotyping) ? 2 : 1;


    // note you can play with the container maxwidth to spread out the window content appears in
    return (
        <Container component="main" maxWidth="sm">
            <Header label={title} />
            <Subtitle>Step {page} of {maxPages}</Subtitle>

            <Grid container spacing={2}>
                <Grid xs={6} sm={6}> 
                </Grid>
            </Grid>
            <FormProvider {...methods}>
                <Form onSubmit={methods.handleSubmit(onSubmit)} onChange={onChange}>
                    <Grid container spacing={2}>

                        {page === 1 && observedStereotyping && <>
                        <Grid xs={12} sm={12}>
                            <FormRadioGroup
                                name="can_stereotypies_be_interrupted"
                                label="Can the stereotypy be interrupted at all (is there anything that distracts them, or makes them stop, showing the stereotypy)?"
                                list={yesNoUnknown}
                                required={true}
                                fullWidth
                            />
                        </Grid>

                        <FormConditionCheck name="can_stereotypies_be_interrupted" value="yes">
                        <Grid xs={12} sm={12}>
                            <FormTextField
                                name="can_stereotypies_be_interrupted_description"
                                label="Please describe what they are interrupted by"
                                required={true}
                                fullWidth
                            />
                        </Grid>
                        </FormConditionCheck>

                        <Grid xs={12} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                            <FormButton onClick={handleNext}>Next</FormButton>
                        </Grid>

                        </>}

                        {page === overallPageReference && <>

                        <Grid xs={12} sm={12}>
                            <Typography>At this moment in time, what is your overall evaluation, of the different aspects of welfare, for this animal?</Typography>
                        </Grid>

                        <Grid xs={12} sm={12}>
                            <CustomSlider 
                                name="mental_health" 
                                label="Mental Health" 
                                leftLabel="Worst imaginable" 
                                rightLabel="Best imaginable for any animal anywhere" 
                                tooltipKey="final_evaluation_mental_health" />
                        </Grid>

                        <Grid xs={12} sm={12}>
                            <CustomSlider 
                                name="physical_health" 
                                label="Physical Health" 
                                leftLabel="Worst imaginable" 
                                rightLabel="Best imaginable for any animal anywhere" 
                                tooltipKey="final_evaluation_physical_health" />
                        </Grid>

                        <Grid xs={12} sm={12}>
                            <CustomSlider 
                                name="overall_welfare" 
                                label="Overall Welfare" 
                                leftLabel="Worst imaginable" 
                                rightLabel="Best imaginable for any animal anywhere" 
                                tooltipKey="final_evaluation_overall_welfare" />
                        </Grid>


                        {observedStereotyping && <Grid xs={6}> 
                            <FormButton onClick={handlePrev}>Prev</FormButton>
                        </Grid>}
                        <Grid xs={(observedStereotyping) ? 6 : 12} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                            <FormButton>Submit/Close Assessment</FormButton>
                        </Grid>
                        </>}

                    </Grid>

                </Form>
            </FormProvider>
        </Container>
    );
}
